/* @flow */
import * as React from "react";
import Bacon from "baconjs";
import prefixStyles from "../../utils/prefixStyles";
import { updateHeatSetpoint, updateCoolSetpoint } from "../calculateSetpoint";
import { withColorsProvider, type ColorSet } from "../../ColorsProvider";
import TileControl from "./TileControl";
import UserLanguageContext from "../../ContextWrappers/UserLanguageContext";

type ThermostatTileControlsProps = {
  colors: ColorSet,
  onChangesStarted?: ?({
    setpointHeat?: number,
    setpointCool?: number,
  }) => void,
  onChangesEnded?: ({
    setpointHeat?: number,
    setpointCool?: number,
  }) => void,
  changeBufferMs: number,
  setpointHeat?: number,
  setpointHeatMin?: number,
  setpointHeatMax?: number,
  setpointCool?: number,
  setpointCoolMin?: number,
  setpointCoolMax?: number,
  setpointIncrement: number,
  isConnected?: ?boolean,
  // the delta is used internally by calculateSetpoint
  setpointDelta: number, // eslint-disable-line react/no-unused-prop-types
  occupancyMode?: string,
  onOccupancyChange?: ?({ occupancyMode: "occupied" | "unoccupied" }) => void,
};

type State = {
  selectedSetpoint?: "heat" | "cool" | null,
  setpointHeat?: number,
  setpointCool?: number,
};

class ThermostatTileControls extends React.Component<
  ThermostatTileControlsProps,
  State
> {
  _isMounted: ?boolean;
  _focusable: ?HTMLElement;
  _heatChanged: ?Bacon.Bus;
  _coolChanged: ?Bacon.Bus;
  _setpointChosen: ?Bacon.Bus;

  static defaultProps = {
    changeBufferMs: 2000,
    setpointDelta: 3,
    setpointIncrement: 1,
  };

  state = {
    selectedSetpoint: null, // "heat", "cool"
    setpointHeat: this.props.setpointHeat, // number
    setpointCool: this.props.setpointCool, // number
  };

  componentDidMount() {
    this._isMounted = true;
    this._setUpBacon();
  }

  UNSAFE_componentWillReceiveProps(nextProps: ThermostatTileControlsProps) {
    this.setState({
      setpointHeat: nextProps.setpointHeat,
      setpointCool: nextProps.setpointCool,
    });
  }

  componentDidUpdate(prevProps: ThermostatTileControlsProps, prevState: State) {
    if (
      prevState.selectedSetpoint !== this.state.selectedSetpoint &&
      this._focusable != null
    ) {
      this._focusable.focus();
    }

    if (prevProps.changeBufferMs !== this.props.changeBufferMs) {
      this._tearDownBacon();
      this._setUpBacon();
    }
  }

  componentWillUnmount() {
    this._tearDownBacon();
    this._isMounted = false;
  }

  _computeIsConnected() {
    const { setpointHeat, setpointCool, isConnected } = this.props;

    if (isConnected != null) {
      return isConnected;
    } else if (setpointHeat || setpointCool) {
      return true;
    }
    return false;
  }

  _setUpBacon() {
    this._heatChanged = new Bacon.Bus();
    this._coolChanged = new Bacon.Bus();
    this._setpointChosen = new Bacon.Bus();

    // This is to make flow happy
    let _heatChanged = this._heatChanged;
    let _coolChanged = this._coolChanged;
    let _setpointChosen = this._setpointChosen;

    _setpointChosen.onValue((value) => {
      this.setState({ selectedSetpoint: value });
    });

    _heatChanged.onValue((value) => {
      this.setState((prevState) => {
        const targetHeatSetpoint = prevState.setpointHeat + value;
        const info = { ...this.props, ...prevState };
        return updateHeatSetpoint(info, targetHeatSetpoint);
      });
    });

    _coolChanged.onValue((value) => {
      this.setState((prevState) => {
        const targetCoolSetpoint = prevState.setpointCool + value;
        const info = { ...this.props, ...prevState };
        return updateCoolSetpoint(info, targetCoolSetpoint);
      });
    });

    const userInteraction = _heatChanged
      .merge(this._coolChanged)
      .merge(this._setpointChosen);

    const startEvents = userInteraction
      .debounceImmediate(this.props.changeBufferMs)
      .map(() => "start");

    const endEvents = userInteraction
      .debounce(this.props.changeBufferMs)
      .map(() => "end");

    startEvents
      .merge(endEvents)
      .skipDuplicates()
      .onValue((event) => {
        const { setpointHeat, setpointCool } = this.state;

        if (event === "start") {
          if (this.props.onChangesStarted) {
            this.props.onChangesStarted({
              setpointHeat,
              setpointCool,
            });
          }
        } else if (event === "end") {
          if (this.props.onChangesEnded) {
            this.props.onChangesEnded({
              setpointHeat,
              setpointCool,
            });
          }
          if (this._isMounted) {
            this.setState({
              selectedSetpoint: null,
            });
          }
        }
      });
  }

  _tearDownBacon() {
    if (this._heatChanged) {
      this._heatChanged.end();
    }
    if (this._coolChanged) {
      this._coolChanged.end();
    }
    if (this._setpointChosen) {
      this._setpointChosen.end();
    }
  }

  handleHeatUp = () => {
    if (this._heatChanged) {
      this._heatChanged.push(this.props.setpointIncrement);
    }
  };
  handleHeatDown = () => {
    if (this._heatChanged) {
      this._heatChanged.push(-this.props.setpointIncrement);
    }
  };
  handleCoolUp = () => {
    if (this._coolChanged) {
      this._coolChanged.push(this.props.setpointIncrement);
    }
  };
  handleCoolDown = () => {
    if (this._coolChanged) {
      this._coolChanged.push(-this.props.setpointIncrement);
    }
  };

  handleSetpointChosen = (kind: "heat" | "cool") => {
    if (this._setpointChosen) {
      this._setpointChosen.push(kind);
    }
  };

  handleShowHeat = () => {
    this.handleSetpointChosen("heat");
  };
  handleShowCool = () => {
    this.handleSetpointChosen("cool");
  };

  getMode() {
    const { setpointHeat, setpointCool } = this.state;
    let mode = "off";

    if (this._computeIsConnected()) {
      if (setpointHeat && !setpointCool) {
        mode = "heat";
      } else if (setpointCool && !setpointHeat) {
        mode = "cool";
      } else if (setpointCool && setpointHeat) {
        mode = "auto";
      }
    }

    return mode;
  }

  render() {
    const {
      colors,
      setpointHeatMin,
      setpointHeatMax,
      setpointCoolMin,
      setpointCoolMax,
      setpointIncrement,
      occupancyMode,
    } = this.props;

    const languageContext = this.context;
    const language = languageContext.localeSet.nx_components.TileControl;

    const { selectedSetpoint, setpointHeat, setpointCool } = this.state;

    const styles = {
      wrapper: {
        color: colors.LABEL,
        textAlign: "center",
        width: "100%",
      },
      content: prefixStyles({
        display: "flex",
        justifyContent: "space-around",
      }),
      occupancyContent: prefixStyles({
        display: "flex",
        justifyContent: "space-around",
        alignItems: "space-around",
      }),
    };

    // Default values for Flow
    let pickerProps = {
      pickerValue: 70,
      setpointMin: 60,
      setpointMax: 80,
    };

    if (selectedSetpoint === "heat") {
      pickerProps = {
        pickerValue: setpointHeat,
        setpointMin: setpointHeatMin,
        setpointMax: setpointHeatMax,
      };
    } else if (selectedSetpoint === "cool") {
      pickerProps = {
        pickerValue: setpointCool,
        setpointMin: setpointCoolMin,
        setpointMax: setpointCoolMax,
      };
    }

    const mode = this.getMode();
    const content =
      mode === "off" ? (
        language.Off
      ) : (
        <TileControl
          selectedSetpoint={selectedSetpoint}
          mode={mode}
          occupancyMode={occupancyMode}
          setpointHeat={setpointHeat}
          setpointCool={setpointCool}
          handleShowHeat={this.handleShowHeat}
          handleShowCool={this.handleShowCool}
          handleHeatUp={this.handleHeatUp}
          handleHeatDown={this.handleHeatDown}
          handleCoolUp={this.handleCoolUp}
          handleCoolDown={this.handleCoolDown}
          setpointIncrement={setpointIncrement}
          {...pickerProps}
        />
      );

    return (
      <div
        data-component-name="ThermostatTileControls"
        style={styles.wrapper}
        aria-live="polite"
      >
        {content}
      </div>
    );
  }
}
ThermostatTileControls.contextType = UserLanguageContext;
export default withColorsProvider(ThermostatTileControls);



// WEBPACK FOOTER //
// ../nx-components/src/Thermostat/TileControls/index.jsx